import { useId, type ComponentProps, type ReactNode } from "react";
import { cn } from "../lib/cn";

export type CheckboxProps = Omit<ComponentProps<"input">, "type"> & {
  label: ReactNode;
  description?: ReactNode;
};

/** Sharp square box, ink when checked. Label and optional description sit to the right and are part of the hit area. */
export function Checkbox({ label, description, className, id, ...props }: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "grid cursor-pointer grid-cols-[16px_1fr] gap-x-3 gap-y-0.5 py-1.5",
        "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
        className,
      )}
    >
      <span className="relative mt-0.5 size-4">
        <input
          id={inputId}
          type="checkbox"
          className={cn(
            "peer size-4 cursor-pointer appearance-none border border-line-strong bg-surface transition-colors duration-150 ease-out-quick",
            "hover:border-ink-3 checked:border-ink checked:bg-ink disabled:cursor-not-allowed",
          )}
          {...props}
        />
        <svg
          aria-hidden
          viewBox="0 0 16 16"
          className="pointer-events-none absolute inset-0 hidden size-4 text-surface peer-checked:block"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="square"
        >
          <path d="M3.5 8.5 6.5 11.5 12.5 5" />
        </svg>
      </span>
      <span className="text-sm text-ink">{label}</span>
      {description && <span className="col-start-2 text-xs text-ink-3">{description}</span>}
    </label>
  );
}
